import React from "react";
import DashboardLayout from "../components/DashboardLayout";
import { useAuth } from "../lib/auth";
import { CheckCircle2, Clock, FileSearch, Rocket, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const stages = [
  { key: "received", label: "Application received", icon: CheckCircle2, body: "We've got it. A reviewer has been assigned." },
  { key: "review",   label: "Document & risk review", icon: FileSearch, body: "We run background checks, carrier compatibility, and risk scoring." },
  { key: "decision", label: "Decision & onboarding", icon: Mail, body: "We email you within one business day. Most applications are approved same-day." },
  { key: "live",     label: "You're live — ship away", icon: Rocket, body: "Wallet is funded, carrier accounts unlocked, Smart Services activated." },
];

export default function PendingApplication() {
  const { user } = useAuth();
  return (
    <DashboardLayout>
      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 px-3 py-1 border border-amber-200 bg-amber-50 rounded-sm text-[10px] sm:text-xs tracking-[0.2em] uppercase font-medium text-amber-700 mb-6" data-testid="pending-app-badge">
          <Clock size={12} /> Application in review
        </div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter text-zinc-900">
          Welcome, {user?.name?.split(" ")[0]}.
        </h1>
        <p className="mt-4 text-zinc-600 text-sm sm:text-base leading-relaxed">
          We're reviewing your application. No action needed on your end — here's what happens next.
        </p>

        <div className="mt-10 space-y-6" data-testid="application-timeline">
          {stages.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === 1; // Document review stage
            const isDone = i === 0;
            return (
              <div key={s.key} className="flex gap-4">
                <div className="relative flex flex-col items-center">
                  <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${
                    isDone ? "brand-bg text-white" : isActive ? "bg-amber-100 text-amber-700 animate-pulse" : "border border-zinc-200 text-zinc-400"
                  }`}>
                    <Icon size={16} strokeWidth={1.8} />
                  </div>
                  {i < stages.length - 1 && <div className={`w-px flex-1 mt-1 ${isDone ? "bg-[var(--brand-accent)]" : "bg-zinc-200"}`} style={{ minHeight: 40 }} />}
                </div>
                <div className="pb-6 flex-1">
                  <div className={`font-display font-bold text-base sm:text-lg ${isDone || isActive ? "text-zinc-900" : "text-zinc-500"}`}>
                    {s.label}
                  </div>
                  <div className="text-sm text-zinc-600 mt-1 max-w-xl">{s.body}</div>
                  {isActive && <div className="mt-2 text-xs brand-text font-medium">In progress — most reviews complete within 1 business day</div>}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 bg-zinc-50 border border-zinc-200 rounded-sm p-5 sm:p-6">
          <div className="font-display font-bold text-zinc-900 mb-2">While you wait</div>
          <div className="text-sm text-zinc-600 mb-4">You can prepare now so the moment you're approved, you ship.</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Link to="/dashboard/addresses" data-testid="pending-cta-addresses" className="border border-zinc-300 bg-white rounded-sm p-4 text-sm hover:border-zinc-400 block">
              <div className="font-display font-bold">Build your address book</div>
              <div className="text-xs text-zinc-600 mt-1">Save warehouses, customers, and frequent destinations.</div>
            </Link>
            <Link to="/dashboard/smart-services" data-testid="pending-cta-services" className="border border-zinc-300 bg-white rounded-sm p-4 text-sm hover:border-zinc-400 block">
              <div className="font-display font-bold">Review Smart Services</div>
              <div className="text-xs text-zinc-600 mt-1">Choose which protection layers you want on every shipment.</div>
            </Link>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
